import React, { useState } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { formatMoney } from '../utils/formatMoney';

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 'var(--radius-ui)',
  border: '1px solid var(--color-border)',
  fontSize: '14px',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-surface)',
  color: 'var(--color-neutral-text-main)',
};

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: '600',
  color: 'var(--color-neutral-text-muted)',
  marginBottom: '6px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const toggleBtnStyle = {
  width: '100%',
  marginTop: '12px',
  padding: '10px 14px',
  fontSize: '14px',
  fontWeight: '600',
  color: 'var(--color-secondary-brand)',
  backgroundColor: 'var(--color-workspace-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-ui)',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'var(--font-sans)',
};

const Search = () => {
  const [searchNo, setSearchNo] = useState('');
  const [searchFileNo, setSearchFileNo] = useState('');
  const [matchMode, setMatchMode] = useState('exact');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();

    const no = searchNo.trim();
    const fileNo = searchFileNo.trim();
    if (!no && !fileNo) {
      setError('Enter at least one value: Entry no. (No.) or File no. (Ref).');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (no) params.append('no', no);
      if (fileNo) params.append('fileNo', fileNo);
      params.append('mode', matchMode);

      const response = await axios.get(
        `${API_URL}/api/ledger/search?${params.toString()}`
      );
      setResults(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to search');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchNo('');
    setSearchFileNo('');
    setMatchMode('exact');
    setResults(null);
    setError(null);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 6px 0' }}>Search</h2>
        <p className="text-muted" style={{ margin: '0 0 20px 0', fontSize: '14px', maxWidth: '560px' }}>
          Look up ledger lines by entry number and/or file reference. Open{' '}
          <strong>Advanced search</strong> for partial matching and more detail.
        </p>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: '16px' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSearch}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '14px',
              marginBottom: '14px',
            }}
          >
            <div>
              <label htmlFor="adv-search-no" style={labelStyle}>
                Entry no. (No.)
              </label>
              <input
                id="adv-search-no"
                type="text"
                placeholder="e.g. 42"
                value={searchNo}
                onChange={(e) => setSearchNo(e.target.value)}
                style={inputStyle}
                autoComplete="off"
              />
            </div>
            <div>
              <label htmlFor="adv-search-fileno" style={labelStyle}>
                File no. (Ref)
              </label>
              <input
                id="adv-search-fileno"
                type="text"
                placeholder="e.g. client reference"
                value={searchFileNo}
                onChange={(e) => setSearchFileNo(e.target.value)}
                style={inputStyle}
                autoComplete="off"
              />
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Searching…' : 'Search'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClear}
              disabled={loading}
            >
              Clear all
            </button>
          </div>

          <button
            type="button"
            style={toggleBtnStyle}
            onClick={() => setShowAdvanced((open) => !open)}
            aria-expanded={showAdvanced}
          >
            {showAdvanced ? '▲ Hide advanced search' : '▼ Show advanced search'}
          </button>

          {matchMode === 'contains' && !showAdvanced && (
            <p className="text-muted" style={{ margin: '10px 0 0 0', fontSize: '12px' }}>
              Partial matching is on. Open advanced search to switch to exact match.
            </p>
          )}

          {showAdvanced && (
            <div
              style={{
                marginTop: '16px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-ui)',
                padding: '18px',
                backgroundColor: 'var(--color-workspace-bg)',
              }}
            >
              <div
                className="text-muted"
                style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: '12px',
                }}
              >
                Advanced options
              </div>
              <p className="text-muted" style={{ margin: '0 0 16px 0', fontSize: '13px', lineHeight: 1.5 }}>
                Use <strong>Contains</strong> for partial, case-insensitive matches. If both Entry no.
                and File no. are filled, both must match (AND).
              </p>
              <div style={{ maxWidth: '320px' }}>
                <label htmlFor="adv-search-mode" style={labelStyle}>
                  Match style
                </label>
                <select
                  id="adv-search-mode"
                  value={matchMode}
                  onChange={(e) => setMatchMode(e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer', backgroundColor: 'white' }}
                >
                  <option value="exact">Exact match</option>
                  <option value="contains">Contains (partial)</option>
                </select>
              </div>
            </div>
          )}
        </form>
      </div>

      {results && (
        <div>
          <div className="summary">
            <div className="summary-card">
              <h3>Total Incoming</h3>
              <div className="value amount-positive">
                {formatMoney(results.summary.totalIncoming)}
              </div>
            </div>
            <div className="summary-card">
              <h3>Total Outgoing</h3>
              <div className="value amount-negative">
                {formatMoney(results.summary.totalOutgoing)}
              </div>
            </div>
            <div className="summary-card">
              <h3>Net Amount</h3>
              <div
                className={`value ${results.summary.netAmount >= 0
                    ? 'amount-positive'
                    : 'amount-negative'
                  }`}
              >
                {formatMoney(results.summary.netAmount)}
              </div>
            </div>
            <div className="summary-card">
              <h3>Total Records</h3>
              <div className="value">{results.summary.totalRecords}</div>
            </div>
          </div>

          <div className="card">
            <h3 style={{ marginBottom: '20px' }}>Results</h3>
            {results.entries.length === 0 ? (
              <p className="text-muted">No records match your criteria.</p>
            ) : (
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>File No</th>
                      <th>Amount</th>
                      <th>Type</th>
                      <th>Reason for payment</th>
                      <th>Year</th>
                      <th>Month</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.entries.map((entry) => {
                      const isPositive = entry.type === 'incoming' || entry.type === 'transfer_in';
                      const isTransfer = entry.type === 'transfer_in' || entry.type === 'transfer_out';

                      return (
                        <tr key={entry.id}>
                          <td>{entry.no || '-'}</td>
                          <td>{entry.fileNo || '-'}</td>
                          <td
                            className={
                              isPositive
                                ? 'amount-positive'
                                : 'amount-negative'
                            }
                          >
                            {formatMoney(entry.amount)}
                          </td>
                          <td>
                            <span
                              className={
                                isPositive
                                  ? isTransfer
                                    ? 'badge badge-transfer-in'
                                    : 'badge badge-incoming'
                                  : isTransfer
                                    ? 'badge badge-transfer-out'
                                    : 'badge badge-outgoing'
                              }
                            >
                              {entry.type.replace('_', ' ')}
                            </span>
                          </td>
                          <td
                            className="text-muted"
                            style={{
                              fontSize: '12px',
                              maxWidth: '220px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={entry.reasonForPayment || ''}
                          >
                            {entry.reasonForPayment || '-'}
                          </td>
                          <td>{entry.year}</td>
                          <td>
                            {new Date(entry.year, entry.month - 1).toLocaleString(
                              'default',
                              { month: 'long' }
                            )}
                          </td>
                          <td>
                            {entry.transactionDate
                              ? new Date(entry.transactionDate).toLocaleDateString()
                              : new Date(entry.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {loading && <div className="loading">Searching…</div>}
    </div>
  );
};

export default Search;
